<template>
  <main>
    <BlokkliProvider
      v-slot="{ entity, isEditing }"
      v-bind="blokkliProps"
      :entity="props"
    >
      <PageHero
        v-if="entity.keyVisualMedia"
        :title="entity.title"
        :subtitle="entity.lead"
        :media="entity.keyVisualMedia"
      />
      <PageHeader
        v-else-if="!headerHidden"
        :title="entity?.title ?? title"
        :subtitle="entity?.lead ?? lead"
      />
      <BlokkliField
        :list="filteredParagraphs"
        name="field_paragraphs"
        :should-render-item
        :allowed-fragments
      />
      <slot :is-editing="isEditing" />
    </BlokkliProvider>
    <SharingButtons v-if="showSharing" />
  </main>
</template>

<script lang="ts" setup>
import type { BlokkliFragmentName } from '#blokkli-build/definitions'
import { DomainId, type NodeEditorialFragment } from '#graphql-operations'
import { filterNullArray, stripHtml } from '~/helpers'

const props = defineProps<NodeEditorialFragment>()
const filteredParagraphs = filterNullArray(props.paragraphs)

const { activeDomain } = useSiteContext()
const requestUrl = useRequestURL()
const route = useRoute()

const { shouldRenderItem } = useParagraphVisiblity()

const headerHidden = computed(
  () => props.isFrontPage || props.isChannelLandingPage,
)

const allowedFragments = computed(() => {
  const fragments: BlokkliFragmentName[] = [
    'anchor_navigation',
    'zurich_card_widget',
    'countdown',
    'tomas_shop',
    'tours_search_form',
    'team_list',
    'ostendis_jobs',
  ]

  // Only available on these two domains.
  if (
    activeDomain === DomainId.eyz ||
    activeDomain === DomainId.rapperswil_zuerichsee_ch
  ) {
    fragments.push('mailchimp_signup_form')
  }

  return fragments
})

if (import.meta.server) {
  useHead({
    script: props.hideUsercentrics
      ? [
          {
            key: 'usercentrics-hide',
            innerHTML: `var UC_UI_SUPPRESS_CMP_DISPLAY = true;`,
            tagPriority: -9999,
          },
        ]
      : undefined,
  })
}

// Collect all FAQ questions from FAQ paragraphs (questions are now included in the fragment)
const faqQuestions = computed(() => {
  const questions: Array<{ name: string; answer: string }> = []
  for (const paragraph of filteredParagraphs) {
    if (
      paragraph.bundle === 'faq' &&
      paragraph.props &&
      'questions' in paragraph.props
    ) {
      const paragraphQuestions = paragraph.props.questions as
        Array<{ name: string; answer: string }> | undefined
      if (paragraphQuestions) {
        questions.push(...paragraphQuestions)
      }
    }
  }
  return questions
})

// Build schema.org structured data
const schemaOrg = computed(() => {
  const pageUrl = requestUrl.origin + route.path

  let schema: object = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
  }

  // Only add FAQPage schema if there are FAQ questions
  if (faqQuestions.value.length > 0) {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      url: pageUrl,
      mainEntity: faqQuestions.value.map((question) => ({
        '@type': 'Question',
        name: stripHtml(question.name),
        acceptedAnswer: {
          '@type': 'Answer',
          text: stripHtml(question.answer),
        },
      })),
    }
  }

  return schema
})

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schemaOrg.value),
    },
  ],
})
</script>
